import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Checkbox } from './ui/checkbox';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from './ui/table';
import { ClipboardList, Loader2, Search, AlertTriangle, CheckCircle2, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { formatDate, cn } from '../lib/utils';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const ChainRequirementReport = () => {
  const [orders, setOrders] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [search, setSearch] = useState('');
  const [running, setRunning] = useState(false);
  const [report, setReport] = useState(null);
  const [allOrders, setAllOrders] = useState(false); // false = use selection, true = all open

  const loadOrders = useCallback(() => {
    axios.get(`${API}/chains/orders-needing`)
      .then(r => setOrders(Array.isArray(r.data) ? r.data : []))
      .catch(() => setOrders([]));
  }, []);

  useEffect(() => { loadOrders(); }, [loadOrders]);

  const filteredOrders = useMemo(() => {
    const t = search.trim().toLowerCase();
    return t
      ? orders.filter(o =>
          (o.order_number || '').toLowerCase().includes(t)
          || (o.customer_name || '').toLowerCase().includes(t)
          || (o.gold_karat || '').toLowerCase().includes(t)
        )
      : orders;
  }, [orders, search]);

  const toggle = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelected(next);
  };
  const toggleAllFiltered = () => {
    const allSelected = filteredOrders.every(o => selected.has(o.id));
    const next = new Set(selected);
    if (allSelected) filteredOrders.forEach(o => next.delete(o.id));
    else filteredOrders.forEach(o => next.add(o.id));
    setSelected(next);
  };

  const run = async () => {
    if (!allOrders && selected.size === 0) {
      toast.error('Tick at least one order or switch to "All open orders"');
      return;
    }
    setRunning(true);
    try {
      const body = allOrders
        ? { order_ids: [], include_dispatched: false }
        : { order_ids: Array.from(selected) };
      const r = await axios.post(`${API}/chains/requirement-report`, body);
      setReport(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed');
    } finally { setRunning(false); }
  };

  return (
    <Card data-testid="chain-requirement-report">
      <CardHeader className="pb-3">
        <CardTitle className="font-heading text-base flex items-center gap-2">
          <ClipboardList className="h-4 w-4 text-violet-500" /> Chain Requirement Report
        </CardTitle>
        <p className="text-xs text-muted-foreground">Pick orders, then run to see required vs stock vs shortfall.</p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex items-center gap-1.5 mr-2">
            <Checkbox
              id="crr-all-toggle"
              checked={allOrders}
              onCheckedChange={(v) => setAllOrders(!!v)}
              data-testid="crr-all-toggle"
            />
            <label htmlFor="crr-all-toggle" className="text-xs">Use all open orders</label>
          </div>
          <Button onClick={run} disabled={running} data-testid="crr-run-btn">
            {running ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Search className="h-4 w-4 mr-1" /> Run Report</>}
          </Button>
          <Button variant="outline" size="sm" onClick={loadOrders} data-testid="crr-refresh-btn">
            <RefreshCw className="h-3.5 w-3.5 mr-1" /> Refresh orders
          </Button>
          <div className="ml-auto text-xs text-muted-foreground">
            {allOrders ? `${orders.length} open order${orders.length !== 1 ? 's' : ''}` : `${selected.size} selected`}
          </div>
        </div>

        {!allOrders && (
          <div className="border rounded-lg">
            <div className="p-2 flex items-center gap-2 border-b bg-muted/30">
              <Input
                placeholder="Filter orders / customer / karat…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="h-8 max-w-xs"
                data-testid="crr-order-filter"
              />
              <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={toggleAllFiltered}>
                {filteredOrders.every(o => selected.has(o.id)) ? 'Unselect all' : 'Select all (filtered)'}
              </Button>
            </div>
            <div className="overflow-y-auto max-h-[260px]">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-8"></TableHead>
                    <TableHead>Order</TableHead>
                    <TableHead>Customer</TableHead>
                    <TableHead className="text-right">Chains Req</TableHead>
                    <TableHead>Karat</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredOrders.length === 0 ? (
                    <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-6 text-xs">No orders need chains.</TableCell></TableRow>
                  ) : filteredOrders.map(o => (
                    <TableRow key={o.id} className="cursor-pointer" onClick={() => toggle(o.id)} data-testid={`crr-row-${o.id}`}>
                      <TableCell><Checkbox checked={selected.has(o.id)} onCheckedChange={() => toggle(o.id)} data-testid={`crr-checkbox-${o.id}`} /></TableCell>
                      <TableCell className="font-mono text-xs font-semibold">{o.order_number}</TableCell>
                      <TableCell className="text-xs">{o.customer_name}</TableCell>
                      <TableCell className="text-right text-xs font-bold">{o.chains_required}</TableCell>
                      <TableCell className="text-xs">{o.gold_karat}</TableCell>
                      <TableCell className="text-xs">
                        <Badge variant="outline" className="text-[10px]">{o.status}</Badge>
                        {o.chains_breakdown_count === 0 && (
                          <Badge variant="outline" className="ml-1 text-[10px] border-amber-300 text-amber-700 bg-amber-50">no SKU breakdown</Badge>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        )}

        {/* Report */}
        {report && (
          <div className="space-y-3" data-testid="crr-report-result">
            <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
              <KPI label="Orders" value={report.totals.orders_count} />
              <KPI label="Required" value={report.totals.required} />
              <KPI label="In Stock" value={report.totals.in_stock} accent="text-emerald-600 dark:text-emerald-400" />
              <KPI label="Shortfall" value={report.totals.shortfall} accent={report.totals.shortfall > 0 ? 'text-rose-600 dark:text-rose-400' : 'text-emerald-600'} />
              <KPI label="Unassigned" value={report.totals.unassigned_pcs} accent={report.totals.unassigned_pcs > 0 ? 'text-amber-600' : 'text-emerald-600'} />
            </div>

            {report.rows.length === 0 ? (
              <p className="text-xs text-muted-foreground text-center py-4">No chain SKU breakdown found on these orders. Add chain rows to orders first.</p>
            ) : (
              <div className="border rounded-lg overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Chain SKU</TableHead>
                      <TableHead>Karat</TableHead>
                      <TableHead className="text-right">Required</TableHead>
                      <TableHead className="text-right">In Stock</TableHead>
                      <TableHead className="text-right">Shortfall</TableHead>
                      <TableHead>Used By</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {report.rows.map(r => (
                      <TableRow key={r.chain_sku_id} className={r.shortfall > 0 ? 'bg-rose-50/40 dark:bg-rose-500/5' : ''} data-testid={`crr-result-${r.chain_sku_id}`}>
                        <TableCell className="text-sm font-semibold">{r.chain_label || r.chain_sku_id}</TableCell>
                        <TableCell className="text-xs">{r.karat}</TableCell>
                        <TableCell className="text-right tabular-nums font-bold">{r.required}</TableCell>
                        <TableCell className="text-right tabular-nums">{r.in_stock}</TableCell>
                        <TableCell className="text-right tabular-nums">
                          {r.shortfall > 0 ? (
                            <Badge className="bg-rose-100 text-rose-700 dark:bg-rose-500/15 dark:text-rose-300 border-rose-300/60 gap-1">
                              <AlertTriangle className="h-3 w-3" /> {r.shortfall}
                            </Badge>
                          ) : (
                            <Badge className="bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300 border-emerald-300/60 gap-1">
                              <CheckCircle2 className="h-3 w-3" /> OK
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-xs text-muted-foreground">
                          {r.orders.slice(0, 3).map(o => o.order_number).join(', ')}
                          {r.orders.length > 3 && <span> +{r.orders.length - 3}</span>}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}

            {report.unassigned_orders.length > 0 && (
              <div className="text-xs text-amber-800 dark:text-amber-300 bg-amber-50/60 dark:bg-amber-500/5 border border-amber-300/40 rounded-lg p-2">
                <strong>{report.unassigned_orders.length}</strong> order(s) have chain pieces without a SKU breakdown.
                <details className="mt-1">
                  <summary className="cursor-pointer">Show</summary>
                  <ul className="list-disc ml-5 mt-1">
                    {report.unassigned_orders.slice(0, 20).map(o => (
                      <li key={o.order_id}>{o.order_number} · {o.customer_name} — {o.unassigned_pcs} unassigned pcs</li>
                    ))}
                  </ul>
                </details>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const KPI = ({ label, value, accent }) => (
  <div className="rounded-lg border bg-muted/30 px-3 py-2">
    <p className="text-[9px] uppercase tracking-wider text-muted-foreground font-bold">{label}</p>
    <p className={cn('text-xl font-bold tabular-nums leading-tight', accent)}>{value}</p>
  </div>
);

export default ChainRequirementReport;
